import { type FC, useCallback, type MouseEventHandler } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import { graphql, useFragment, useRelayEnvironment } from 'react-relay';

import serverVars from 'server-vars';
import { usePrerenderedCheckout } from 'dibs-checkout/exports/prerender/usePrerenderedCheckout.ts';
import { hidePage } from 'dibs-buyer-layout/exports/hidePage.ts';
import { trackPurchaseClick, type Location } from '../../tracking/actionButtonsTracking.ts';

import { Button } from 'dibs-elements/exports/Button.tsx';
import classnames from 'classnames';
import dibsCss from 'dibs-css';
import { usePdpButtonAbTest } from '../../PdpSharedButtons/pdpButtonAbTest.ts';
import { type PdpSharedBuyItNowButton_item$key } from './__generated__/PdpSharedBuyItNowButton_item.graphql.ts';

const itemFragment = graphql`
    fragment PdpSharedBuyItNowButton_item on Item {
        ...usePrerenderedCheckout_item
        ...actionButtonsTracking_item
    }
`;

const forcedCurrency = serverVars.get('forcedCurrency');

export const PdpSharedBuyItNowButton: FC<{
    item: PdpSharedBuyItNowButton_item$key;
    skuId?: string;
    quantitySelected?: string;
    disabled?: boolean;
    size?: 'small' | 'medium' | 'large';
    location?: Location;
    className?: string;
}> = ({
    item: itemRef,
    skuId,
    quantitySelected,
    disabled,
    size = 'large',
    location,
    className,
}) => {
    const environment = useRelayEnvironment();
    const item = useFragment(itemFragment, itemRef);

    const { redirect: redirectToCheckout } = usePrerenderedCheckout({
        hideParentPage: hidePage,
        item,
        params: {
            skuId,
            quantity: quantitySelected,
            offerCurrency: forcedCurrency,
            triggerType: location === 'STICKY_HEADER' ? 'pdp-header-purchase' : undefined,
        },
    });

    const onClick: MouseEventHandler = useCallback(
        event => {
            trackPurchaseClick({
                item,
                event,
                location,
                environment,
                quantity: Number(quantitySelected),
            });
            redirectToCheckout();
        },
        [item, location, environment, quantitySelected, redirectToCheckout]
    );

    const { isActive: abTestActive, colors: abTestColors } = usePdpButtonAbTest();

    return (
        <Button
            fullWidth
            type="primary"
            size={size}
            onClick={onClick}
            disabled={disabled}
            className={classnames(className, {
                [dibsCss.pxMedium]: abTestActive,
                [dibsCss.py0]: abTestActive,
                [dibsCss.normalCase]: abTestActive,
            })}
            dataTn="pdp-shared-buy-it-now-button"
            style={abTestActive ? 'circle' : undefined}
            customColors={abTestActive ? abTestColors : undefined}
        >
            <FormattedMessage id="PdpSharedBuyItNowButton.buyItNow" defaultMessage="Buy it now" />
        </Button>
    );
};
